import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

export default function QAPage() {
  const { t } = useTranslation('qa');

  const title = t('qa.title', 'Q&A');
  const lastUpdated = t('qa.last_updated');
  const questions = t('qa.items', { returnObjects: true });

  // State to manage which accordion item is open
  const [openIndex, setOpenIndex] = useState(null);

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Define all styles as a JavaScript object with responsiveness in mind
  const styles = {
    container: {
      maxWidth: '900px', // Still keeping a max-width for large screens
      width: '95%', // Make width fluid
      margin: '2rem auto',
      padding: 'clamp(1rem, 5vw, 1.5rem)', // Responsive padding
      backgroundColor: '#f9f9f9',
      borderRadius: '10px',
      boxShadow: '0 6px 18px rgba(0,0,0,0.08)',
      fontFamily: 'Inter, sans-serif',
      boxSizing: 'border-box', // Ensure padding is included in the width
    },
    pageTitle: {
      fontSize: 'clamp(2rem, 5vw, 2.8rem)', // Responsive font size
      fontWeight: '700',
      color: '#29B185',
      textAlign: 'center',
      marginBottom: '1rem',
      letterSpacing: '-0.02em',
    },
    lastUpdated: {
      fontSize: 'clamp(0.8rem, 2vw, 0.95rem)', // Responsive font size
      color: '#777',
      textAlign: 'center',
      marginBottom: 'clamp(1rem, 2vw, 1.5rem)', // Responsive margin-bottom
    },
    qaItem: {
      backgroundColor: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      padding: 'clamp(1rem, 4vw, 1.5rem)', // Responsive padding
      marginBottom: 'clamp(1rem, 3vw, 1.5rem)', // Responsive margin-bottom
      boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    },
    question: {
      fontSize: 'clamp(1.2rem, 3vw, 1.4rem)', // Responsive font size
      fontWeight: '600',
      color: '#2c3e50',
      marginBottom: '0.8rem', // Keeping this fixed for consistent spacing within header
      lineHeight: '1.4',
    },
    answer: {
      fontSize: 'clamp(1.2rem, 3vw, 1.4rem)', // Responsive font size
      color: '#555',
      lineHeight: '1.6',
    },
    accordionHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      cursor: 'pointer',
      paddingBottom: '0.8rem', // Keeping this fixed for consistent spacing within header
      borderBottom: '1px solid #eee',
      marginBottom: '0.8rem', // Keeping this fixed for consistent spacing within header
      flexWrap: 'wrap', // Allow items to wrap on small screens
    },
    accordionHeaderH3: {
      margin: 0,
      flexGrow: 1,
      paddingRight: 'clamp(0.5rem, 2vw, 1rem)', // Responsive padding before the icon
      fontSize: 'clamp(1.2rem, 3vw, 1.4rem)', // Ensure h3 inside header is also responsive
    },
    accordionIcon: {
      fontSize: 'clamp(1.2rem, 3vw, 1.5rem)', // Responsive icon size
      color: '#29B185',
      transition: 'transform 0.3s ease',
      flexShrink: 0, // Prevent icon from shrinking
    },
    accordionIconOpen: {
      transform: 'rotate(90deg)',
    },
    accordionContent: {
      maxHeight: 0,
      overflow: 'hidden',
      transition: 'max-height 0.5s ease-out, opacity 0.5s ease-out',
      opacity: 0,
    },
    accordionContentOpen: {
      maxHeight: '500px', // This height should be sufficient for most answers. Consider dynamic height if answers vary wildly.
      opacity: 1,
    },
    error: {
      color: 'red',
      textAlign: 'center',
      marginTop: '20px',
      fontSize: '1.2rem',
    }
  };

  if (!Array.isArray(questions)) {
    return <div style={styles.error}>Error: Q&A data not loaded or incorrect format.</div>;
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.pageTitle}>{title}</h1>
      <p style={styles.lastUpdated}><em>{lastUpdated}</em></p>
            <p style={styles.lastUpdated}>{t('Ver. 1.0')}</p>


      {questions.map((item, index) => (
        <div key={index} style={styles.qaItem}>
          {/* Accordion Header */}
          <div style={styles.accordionHeader} onClick={() => toggleAccordion(index)}>
            <h3 style={styles.accordionHeaderH3}>Q: {item.question}</h3>
            <span
              style={{
                ...styles.accordionIcon,
                ...(openIndex === index ? styles.accordionIconOpen : {})
              }}
            >
              &#9654; {/* Unicode right-pointing triangle for icon */}
            </span>
          </div>

          {/* Accordion Content */}
          <div
            style={{
              ...styles.accordionContent,
              ...(openIndex === index ? styles.accordionContentOpen : {})
            }}
          >
            <p style={styles.answer}>A: {item.answer}</p>
          </div>
        </div>
      ))}
    </div>
  );
}